import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { MyPermissions, TenantDetail } from '@up2d8/contracts';
import { LoadingState } from '@up2d8/ui';
import { useSession } from '../../shared/session';
import { formatNumber } from '../../shared/format';
import {
  AdminIcon,
  AdminPage,
  Card,
  CardHead,
  CardPad,
  Empty,
  Grid,
  Note,
  PageHead,
  Stats,
  Table,
} from '../admin-kit';
import { useAccountStats, useBilling } from './master-data';
import { soortLabel, vandaagActief, wanneer } from './platform-format';
import { BillingStats, TenantLink } from './subscriptions-page';
import './huisstijl.css';

/**
 * De start van het platformbeheer (P1): kerncijfers, wat er recent gebeurde en je rechten.
 *
 * Alles komt uit wat er al is: de bedrijvenlijst (`/master/organisaties`, tot honderd bedrijven) en
 * je eigen rechten (`/me/permissions`). Een auditspoor over alle bedrijven heen bestaat niet; "Recent
 * op het platform" toont daarom wat de bedrijvenlijst zelf weet - aangemaakt, opgeschort en de
 * laatste activiteit - tot dat endpoint er is (backend-werk, zie deviations.md).
 */
export function PlatformStartPage() {
  const { t } = useTranslation();
  const { api, has } = useSession();
  const magBedrijven = has('tenants.tenant.manage');
  const magAbonnementen = has('tenants.subscription.manage');
  // MST-5: gebruikers over alle organisaties heen, en (MST-6) de omzet en wie aandacht vraagt.
  const accountStats = useAccountStats();
  const billing = useBilling();
  const gebruikers = (accountStats.data ?? []).reduce((sum, row) => sum + row.active, 0);
  const actief7d = (accountStats.data ?? []).reduce((sum, row) => sum + row.active7d, 0);
  const aandacht = [...billing.rows]
    .filter((row) => row.signals.length > 0)
    .sort((a, b) => (b.signals[0]?.weight ?? 0) - (a.signals[0]?.weight ?? 0))
    .slice(0, 6);

  const permissions = useQuery({
    queryKey: ['access', 'my-permissions'],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/me/permissions');
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as MyPermissions;
    },
  });

  const tenants = useQuery({
    queryKey: ['platform', 'tenants', 'start'],
    enabled: magBedrijven,
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/platform/tenants', {
        params: { query: { limit: 100 } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as { items: TenantDetail[] }).items;
    },
  });

  const rechten = permissions.data?.policyKeys ?? [];
  // Het archief telt niet mee (MST-2).
  const lijst = (tenants.data ?? []).filter((tenant) => tenant.status !== 'archived');
  const demo = lijst.filter((tenant) => tenant.kind !== null).length;
  const opgeschort = lijst.filter((tenant) => tenant.status === 'suspended');

  return (
    <AdminPage>
      <PageHead
        title={t(groet())}
        sub={t('beheer.platform.start.sub', { count: rechten.length })}
      />

      {magBedrijven ? (
        <Card label={t('beheer.platform.start.figures')}>
          <CardPad>
            {tenants.isPending ? (
              <LoadingState rows={1} label={t('common.loading')} />
            ) : tenants.isError ? (
              <Note tone="danger" role="alert">
                {t('errors.generic')}
              </Note>
            ) : (
              <Stats
                label={t('beheer.platform.start.figures')}
                items={[
                  {
                    label: t('tenants.title'),
                    value: formatNumber(lijst.length),
                    sub: t('beheer.platform.start.tenantsSub', {
                      customers: lijst.length - demo,
                      demo,
                    }),
                    testId: 'stat-tenants',
                  },
                  {
                    label: t('master.start.users'),
                    value: formatNumber(gebruikers),
                    sub: t('master.start.usersSub', { count: actief7d }),
                    testId: 'stat-users',
                  },
                  {
                    label: t('beheer.platform.start.activeToday'),
                    value: formatNumber(lijst.filter((tenant) => vandaagActief(tenant)).length),
                    sub: t('beheer.platform.start.activeTodaySub'),
                  },
                  {
                    label: t('tenants.suspended'),
                    value: formatNumber(opgeschort.length),
                    sub:
                      opgeschort.length === 0
                        ? t('beheer.platform.start.noneSuspended')
                        : opgeschort.map((tenant) => tenant.name).join(', '),
                  },
                ]}
              />
            )}
          </CardPad>
        </Card>
      ) : null}

      {magAbonnementen && billing.totals ? (
        <Card label={t('master.billing.figures')}>
          <CardPad>
            <BillingStats rows={billing.rows} />
          </CardPad>
        </Card>
      ) : null}

      <Grid variant="two">
        {magAbonnementen ? (
          <Card>
            <CardHead
              title={t('master.start.attention')}
              count={aandacht.length}
              actions={
                <Link to="/master/abonnementen" className="a-link">
                  {t('master.start.toBilling')}
                </Link>
              }
            />
            {billing.pending ? (
              <CardPad>
                <LoadingState rows={3} label={t('common.loading')} />
              </CardPad>
            ) : aandacht.length === 0 ? (
              <Empty
                icon={<AdminIcon name="sparkle" size={24} />}
                title={t('master.billing.noUpsell')}
              />
            ) : (
              <Table label={t('master.start.attention')}>
                <tbody>
                  {aandacht.map((row) => (
                    <tr key={row.tenant.id}>
                      <td>
                        <TenantLink row={row} />
                      </td>
                      <td className="a-meta">
                        {t(`master.billing.signal.${row.signals[0]!.kind}`, row.signals[0]!.values)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        ) : null}
        {magBedrijven ? <RecentCard tenants={lijst} pending={tenants.isPending} /> : null}

        <Card>
          <CardHead title={t('beheer.platform.start.rights')} count={rechten.length} />
          <CardPad>
            {permissions.isPending ? (
              <LoadingState rows={3} label={t('common.loading')} />
            ) : permissions.isError ? (
              <Note tone="danger" role="alert">
                {t('errors.generic')}
              </Note>
            ) : (
              <ul className="a-rights" data-testid="platform-rights">
                {rechten.map((key) => (
                  <li key={key} className="a-check">
                    <AdminIcon name="check" size={16} strokeWidth={2.5} />
                    <span>
                      {t(`beheer.platform.rights.${key.replace(/\./g, '_')}`, {
                        defaultValue: key,
                      })}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardPad>
        </Card>
      </Grid>
    </AdminPage>
  );
}

/** "Goedemorgen" tot twaalf uur, "Goedemiddag" tot zes uur, daarna "Goedenavond". */
function groet(now = new Date()): string {
  const uur = now.getHours();
  if (uur < 12) return 'beheer.platform.start.morning';
  if (uur < 18) return 'beheer.platform.start.afternoon';
  return 'beheer.platform.start.evening';
}

interface Gebeurtenis {
  tenant: TenantDetail;
  wat: string;
  wanneer: string;
}

/**
 * "Recent op het platform": de laatste zes dingen die de bedrijvenlijst kent, nieuwste eerst.
 *
 * Een bedrijf aangemaakt, een bedrijf opgeschort, en de laatste activiteit in een bedrijf. Wat er
 * precies gebeurde ("functie Gesprekken aangezet") staat in het auditspoor van dat bedrijf, en dat
 * is per bedrijf.
 */
function RecentCard({ tenants, pending }: { tenants: TenantDetail[]; pending: boolean }) {
  const { t } = useTranslation();

  const gebeurtenissen: Gebeurtenis[] = tenants
    .flatMap((tenant) => [
      { tenant, wat: t('beheer.platform.start.eventCreated'), wanneer: tenant.createdAt },
      ...(tenant.suspendedAt
        ? [{ tenant, wat: t('beheer.platform.start.eventSuspended'), wanneer: tenant.suspendedAt }]
        : []),
      ...(tenant.lastActivityAt
        ? [
            {
              tenant,
              wat: t('beheer.platform.start.eventActivity'),
              wanneer: tenant.lastActivityAt,
            },
          ]
        : []),
    ])
    .sort((a, b) => b.wanneer.localeCompare(a.wanneer))
    .slice(0, 6);

  return (
    <Card>
      <CardHead title={t('beheer.platform.start.recent')} />
      {pending ? (
        <CardPad>
          <LoadingState rows={3} label={t('common.loading')} />
        </CardPad>
      ) : gebeurtenissen.length === 0 ? (
        <Empty
          icon={<AdminIcon name="history" size={24} />}
          title={t('beheer.platform.start.recentEmpty')}
        />
      ) : (
        <Table label={t('beheer.platform.start.recent')}>
          <thead>
            <tr>
              <th>{t('beheer.platform.start.colTenant')}</th>
              <th>{t('beheer.platform.start.colWhat')}</th>
              <th>{t('beheer.platform.start.colWhen')}</th>
            </tr>
          </thead>
          <tbody>
            {gebeurtenissen.map((item) => (
              <tr key={`${item.tenant.id}-${item.wat}`}>
                <td>
                  <Link
                    to="/master/organisaties/$tenantId"
                    params={{ tenantId: item.tenant.id }}
                    className="a-name a-link"
                  >
                    {item.tenant.name}
                  </Link>
                  {item.tenant.kind ? (
                    <div className="a-meta">{soortLabel(item.tenant.kind, t)}</div>
                  ) : null}
                </td>
                <td>{item.wat}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{wanneer(item.wanneer, t)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}
